import React, {useEffect, useState, Fragment} from 'react'
import * as PropTypes from 'prop-types'
import I18n from 'i18n-js'
import yup from '../../../lib/YupValidations'
import styled from 'styled-components';

import {Auth} from 'aws-amplify'
import {FacebookLoginButton, GoogleLoginButton} from 'react-social-login-buttons';
import Form from '../../shared/Form'
import ExternalLink from '../../shared/ExternalLink'
import Link from '../../shared/Link'
import Button from '../../shared/Button'
import DelimiterLine from '../../shared/DelimiterLine';
import fonts from '../../../lib/Fonts';
import ServerErrorMessage from '../../shared/Form/ServerErrorMessage';

const initialFormFields = [
  {
    id: 'email',
    label: I18n.t('shared.emailAddress'),
    name: 'email',
    type: 'email',
    validation: yup
      .string()
      .email()
      .required(),
  },
  {
    id: 'password',
    label: I18n.t('shared.password'),
    name: 'password',
    type: 'password',
    customValidation: false,
    validation: yup.string().required('Password is required.'),
  },
];

const generateAdditionFieldsWithValidation = additionalFields => {
  return additionalFields.map(fieldName => {
    return {
      id: fieldName,
      label: I18n.t(`challengeFields.${fieldName}`),
      name: fieldName,
      validation: yup.string().required(),
    }
  });
};

const StyledFacebookButton = styled(FacebookLoginButton)`
  width: 100% !important;
  padding: 10px !important;
  margin: 15px 0 0 0 !important;
  font-size: 1em !important;
  font-family: ${fonts.family.workSans};
`;

const StyledGoogleButton = styled(GoogleLoginButton)`
  width: 100% !important;
  padding: 10px !important;
  margin: 15px 0 0 0 !important;
  font-size: 1em !important;
  font-family: ${fonts.family.workSans};
`;

const LoginForm = ({
                     accountType,
                     additionalFields,
                     challengeName,
                     signIn,
                     isLoading,
                     confirmSignIn,
                     completeNewPassword,
                     externalPasswordLink,
                     resetState,
                     redirectUrl,
                     errorMessage,
                     emailError
                   }) => {
  const [fields, setFields] = useState(initialFormFields);

  useEffect(() => {
    //Resetting state on mount of login component
    resetState();
  }, []);

  useEffect(() => {
    const newFormFields =
      additionalFields.length > 0
        ? generateAdditionFieldsWithValidation(additionalFields)
        : [];
    const allFormFields = initialFormFields.concat(newFormFields);

    if (challengeName === 'SMS_MFA' || challengeName === 'SOFTWARE_TOKEN_MFA') {
      allFormFields.push({
        id: 'code',
        label: I18n.t(`challengeFields.mfaCode`),
        name: 'code',
        validation: yup.string().required(),
      })
    }
    setFields(allFormFields)
  }, [additionalFields, challengeName]);

  const handleSubmit = (args, challengeName) => {
    switch (challengeName) {
      case 'SMS_MFA':
      case 'SOFTWARE_TOKEN_MFA':
        confirmSignIn(args);
        break;
      case 'NEW_PASSWORD_REQUIRED':
        completeNewPassword(args);
        break;
      default:
        signIn({...args, redirectUrl, accountType});
        break
    }
  };

  return (
    <Fragment>
      <Form
        emailError={emailError}
        errorMessage={errorMessage}
        fields={fields}
        onSubmit={(...args) => {
          handleSubmit(...args, challengeName)
        }}
      >
        {errorMessage && (<ServerErrorMessage text={errorMessage}/>)}
        {externalPasswordLink && (
          <ExternalLink href='https://auth.bevspot.com/forgot-password' size='full'>
            {I18n.t('auth.forgotPassword')}
          </ExternalLink>
        )}
        {!externalPasswordLink && (
          <Link size={'full'} to='/forgot-password'>
            {I18n.t('auth.forgotPassword')}
          </Link>
        )}
        <Button
          data-testid={'loginSubmitButton'}
          disabled={isLoading}
          isLoading={isLoading}
          size={'full'}
          style={{marginTop: '24px'}}
          type={'submit'}
        >
          {I18n.t('auth.login')}
        </Button>
        {/* Temporarily hiding social sign in buttons APP2-681 */}
        {/* TODO: Remove 'false && ' below when we want to release social sign in buttons */}
      </Form>{(false && !externalPasswordLink) && (<>
      <DelimiterLine margin={15} type={'lineWithText'}/>
      <StyledFacebookButton onClick={() => Auth.federatedSignIn({provider: 'Facebook'})} size={'full'}>
        <span>Sign In with Facebook</span>
      </StyledFacebookButton>
      <StyledGoogleButton onClick={() => Auth.federatedSignIn({provider: 'Google'})} size={'full'}>
        <span>Sign In with Google</span>
      </StyledGoogleButton>
    </>)
    }
    </Fragment>
  )
};

LoginForm.propTypes = {
  accountType: PropTypes.string,
  additionalFields: PropTypes.arrayOf(PropTypes.string),
  challengeName: PropTypes.string,
  completeNewPassword: PropTypes.func,
  confirmSignIn: PropTypes.func,
  emailError: PropTypes.bool,
  errorMessage: PropTypes.string,
  externalPasswordLink: PropTypes.bool,
  isLoading: PropTypes.bool,
  redirectUrl: PropTypes.string,
  resetState: PropTypes.func,
  signIn: PropTypes.func,
  signInFacebook: PropTypes.func,
  signInGoogle: PropTypes.func,
};

LoginForm.defaultProps = {
  additionalFields: [],
  externalPasswordLink: false, // true for HubspotWidget
  signIn: () => {
  },
  resetState: () => {
  },
};

export default LoginForm;
